import React from "react";
import { Container } from "react-bootstrap";
import ScrollSpy from "react-ui-scrollspy";
import MenuNavigation from "../navigation/MenuNavigation.js";
import ProductCategory from "./ProductCategory.js";
import ProductCategoryLoader from "./ProductCategoryLoader.js";
import ProductModal from "../product-modal/ProductModal.js";
import MenuCartRail from "../cart-rail/MenuCartRail.js";
import LocationAlertModal from "../../../common/ui/LocationAlertModal.js";
import ErrorModal from "../../../common/ui/ErrorModal.js";
import OfflineModal from "../../../common/ui/OfflineModal.js";
import WardModal from "../ward-modal/WardModal.js";
import MarketingBanner from "../../../common/ui/MarketingBanner.js";
import "../../../index.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import {ReactComponent as S8PoweredByLogo} from "../../../images/S8PoweredLogo_Black.svg";

export default function MenuPage(props) {

  /* ⚠ KEPT FROM THE PRE-REDESIGN CODE — do not remove as a duplicate of the
     --wo-rail-h observer below. Two measurements, two stylesheets, both live:

       --s8HeaderHeight   published here, consumed by index.css (scroll-padding-top
                          and the category strip's sticky top)
       --wo-head-h        published by MenuNavigation, consumed by theme.css
       --wo-rail-h        published below, consumed by theme.css

     index.css is still bundled, so dropping this one does not error — it quietly
     falls back to its 54px default and the scroll offset goes wrong on a header
     that is actually 143px. Publishing the same height as the header height
     varies by mode (the pickup/delivery switch and the delivery estimate each add
     a row) is exactly why it is measured rather than hardcoded. */
  React.useEffect(() => {
    const header = document.querySelector("header");
    if (!header) return;
    const setHeaderHeight = () => document.documentElement.style.setProperty("--s8HeaderHeight", `${header.offsetHeight}px`);
    setHeaderHeight();
    const resizeObserver = new ResizeObserver(setHeaderHeight);
    resizeObserver.observe(header);
    return () => resizeObserver.disconnect();
  }, []);

  /* The basket rail sticks below BOTH the header and the category rail, so it
     needs the rail's height. Measured rather than guessed — a hardcoded header
     height was already 25px wrong and left the category rail parked behind the
     header. Published as --wo-rail-h; the header publishes --wo-head-h the same
     way from MenuNavigation. */
  const railRef = React.useRef(null);
  React.useEffect(() => {
    const el = railRef.current;
    if (!el) { return; }
    const apply = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) { document.documentElement.style.setProperty('--wo-rail-h', `${h}px`); }
    };
    apply();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', apply);
      return () => window.removeEventListener('resize', apply);
    }
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  });

  /* --- Menu search ---------------------------------------------------------
     Client-side, over product name and description only. Filtering happens here
     rather than in App.js so the unfiltered productData is left untouched and
     clearing the box is instant. Categories with nothing left are dropped, so
     the user never scrolls past an empty heading. */
  const term = (props.menuSearchTerm || "").trim().toLowerCase();
  const visibleData = term === ""
    ? props.productData
    : props.productData
        .map(cat => ({
          ...cat,
          products: cat.products.filter(p =>
            (p.productName || "").toLowerCase().includes(term) ||
            (p.productDescription || "").toLowerCase().includes(term))
        }))
        .filter(cat => cat.products.length > 0);

  // Category rail — horizontal pills, identical on mobile and desktop.
  const scrollSpyProdCatItems = visibleData.map((item) => {
    return (
      <a
        key={item.productCategoryId}
        href={`#prodcat${item.productCategoryId}`}
        id={`scrollspyprodcat${item.productCategoryId}`}
        className="text-decoration-none scrollspySelector wo-rail__pill"
        data-to-scrollspy-id={`prodcat${item.productCategoryId}`}
        onClick={props.handleScrollspySelectorClick}
        >
        <small className="fw-medium text-secondary">{item.productCategoryName}</small>
      </a>
    )
  })
  
  /* Sticky category rail — horizontal at every size, replacing the old left-hand
     list that needed two separate layouts.
     ⚠ The id navScrollSpyBtns and the data-to-scrollspy-id attributes must not
     change. Bootstrap's scrollspy and react-ui-scrollspy both target them by
     name, and renaming breaks category tracking silently. */
  const scrollSpyProdCat =
    <nav id="navScrollSpyBtns" className="wo-rail" ref={railRef}>
      <div className="wo-rail__inner nav nav-pills flex-nowrap scrollSpyResponsive">
        {scrollSpyProdCatItems}
      </div>
    </nav>

  // Menu Product Category & Products
  const productCategoryMenu = visibleData.map(item => {
    return (
      <ProductCategory
      key={item.productCategoryId}
      data={item}
      handleProductMenuClick={props.handleProductMenuClick}
      />
    );
  });

  const noResults =
    <div className="wo-noresults">
      <p className="wo-noresults__head">Nothing matches &ldquo;{term}&rdquo;</p>
      <p className="wo-noresults__sub">Try a shorter word, or check the spelling.</p>
    </div>;

  /* ⚠ The category rail is rendered OUTSIDE the two-column grid, by the caller
     below — not here. Inside the grid it was confined to the menu column and
     the last categories were clipped at the basket's edge. It now spans the
     full container width, running above the basket as well. */
  const productMenu =
    <>
      <div className="wo-menu" data-bs-spy="scroll" data-bs-target="#navScrollSpyBtns" data-bs-smooth-scroll="true" data-bs-offset="100" tabIndex="0">
        {visibleData.length === 0
          ? noResults
          : <ScrollSpy useBoxMethod={true} offsetTop={0} scrollThrottle={300}>
              {productCategoryMenu}
            </ScrollSpy>}
        <div className="wo-poweredBy">
          <a href="https://www.shift8.com/" target="_blank" rel="noreferrer">
            <svg role="img" width="80px" height="28px" aria-labelledby="Powered by Shift8">
              <title>Powered by Shift8</title>
              <S8PoweredByLogo />
            </svg>
          </a>
        </div>
      </div>
    </>;
  
  return (
    <>
      <MenuNavigation
        data={props.fullMenuData}
        getGeoLocation={props.getGeoLocation} 
        locationList={props.locationList} 
        locationLoading={props.locationLoading} 
        locationSearchInputRef={props.locationSearchInputRef}
        handleFocusSearchLocation={props.handleFocusSearchLocation}
        handleSearchLocation={props.handleSearchLocation}
        handleClearSearchLocation={props.handleClearSearchLocation}
        handleToggleOpenLocation={props.handleToggleOpenLocation}
        handleSelectLocation={props.handleSelectLocation} 
        currentLocation={props.currentLocation}
        showLocModal={props.showLocModal}
        handleShowLocModal={props.handleShowLocModal}
        handleCloseLocModal={props.handleCloseLocModal}
        locationError={props.locationError}
        menuError={props.menuError}
        amountInCart={props.amountInCart}
        handleViewCart={props.handleViewCart}
        urlConfig={props.urlConfig}
        // --- Menu search (redesign) ---
        menuSearchTerm={props.menuSearchTerm}
        handleMenuSearch={props.handleMenuSearch}
        // --- Website Config (S8MC) --- for the uploaded logo in the header
        websiteConfig={props.websiteConfig}
        // Pickup - Shown in Location
        handleBackPickupLocationsToMethodClick={props.handleBackPickupLocationsToMethodClick}
        // --- Pickup / Delivery ---
        pickupOrDelivery={props.pickupOrDelivery}
        handleSelectMethodPickupDeliveryModal={props.handleSelectMethodPickupDeliveryModal}
        handleBackAddressToMethodClick={props.handleBackAddressToMethodClick}
        handleChangeAddressFields={props.handleChangeAddressFields}
        handleSubmitAddressFields={props.handleSubmitAddressFields}
        handlePickupDeliverySwitch={props.handlePickupDeliverySwitch}
        handleCloseMethodChangeModal={props.handleCloseMethodChangeModal}
        handleConfirmMethodChange={props.handleConfirmMethodChange}
        // --- Loyalty Member ---
        loyaltyMember={props.loyaltyMember}
        handleClickOpenLoyaltyModal={props.handleClickOpenLoyaltyModal}
        handleClickCloseLoyaltyModal={props.handleClickCloseLoyaltyModal}
        // Login Page
        handleClickLoginLoyaltyMobile={props.handleClickLoginLoyaltyMobile}
        // Mobile Page
        handleChangeMobileFields={props.handleChangeMobileFields}
        handleNextValidateMobile={props.handleNextValidateMobile}
        // OTP Page
        handleBackOtpPage={props.handleBackOtpPage}
        handleChangeOtpFields={props.handleChangeOtpFields}
        handleResendOtpToMobile={props.handleResendOtpToMobile}
        handleNextValidateOtp={props.handleNextValidateOtp}
        // Voucher List Page
        handleLoyaltyLogout={props.handleLoyaltyLogout}
        handleSelectVoucher={props.handleSelectVoucher}
        // Item Voucher Parent Page
        handleBackItemVoucherParent={props.handleBackItemVoucherParent}
        handleSelectGroupItemVoucherParent={props.handleSelectGroupItemVoucherParent}
        handleAddVoucherToCartItemVoucherParent={props.handleAddVoucherToCartItemVoucherParent}
        // Item Voucher Product List Page
        handleBackItemVoucherProductList={props.handleBackItemVoucherProductList}
        handleSelectProductItemVoucherProductList={props.handleSelectProductItemVoucherProductList}
        // Item Voucher Child Page
        handleBackItemVoucherChild={props.handleBackItemVoucherChild}
        handleModChangeItemVoucherChild={props.handleModChangeItemVoucherChild}
        handleAddToVoucherItemVoucherChild={props.handleAddToVoucherItemVoucherChild}
      />
      {/* Two columns on wide screens: menu left, basket rail right. The rail
          collapses away below 1100px — see theme.css — where the header's
          Basket button takes over. */}
      <Container fluid="lg" className="px-3 position-relative">
        {!props.productsLoading && scrollSpyProdCat}
        <div className="wo-menuGrid">
          <div className="wo-menuGrid__main">
            {/* The marketing banner sits HERE — in the menu column, above the
                first category — because that is where the agreed mockup puts it
                (`.feature` in "Web Ordering Overhaul"). It was first built as a
                strip above the whole header, which looked nothing like it. In
                this column it scrolls with the menu and never competes with the
                sticky header or the category rail. */}
            <MarketingBanner websiteConfig={props.websiteConfig} />
            {props.productsLoading ? <ProductCategoryLoader
            data={[props]} /> : productMenu}
          </div>
          <MenuCartRail
            cartDetails={props.cartDetails}
            cartError={props.cartError}
            handleQuantityChange={props.handleQuantityChange}
            handleRemoveProdFromCart={props.handleRemoveProdFromCart}
            handleViewCart={props.handleViewCart}
          />
        </div>
      </Container>
      <ProductModal 
        // General
        productModalDisplay={props.productModalDisplay}
        addToCartError={props.addToCartError}
        handleAddProductToCart={props.handleAddProductToCart}
        handleProductModalClose={props.handleProductModalClose}
        handleProductFormAddQuantity={props.handleProductFormAddQuantity}
        handleProductFormSubtractQuantity={props.handleProductFormSubtractQuantity}
        currentLocation={props.currentLocation}
        // Product
        productDetails={props.productDetails} 
        productFormData={props.productFormData}
        handleProductFormChange={props.handleProductFormChange}
        handleModFormChange={props.handleModFormChange}
        // Product Combo
        productComboChildDetails={props.productComboChildDetails}
        productComboChildFormData={props.productComboChildFormData}
        handleProductComboChildFormChange={props.handleProductComboChildFormChange}
        handleProductComboModFormChange={props.handleProductComboModFormChange}
        handleProductComboGroupSelect={props.handleProductComboGroupSelect}
        handleBackProductComboGroupSelect={props.handleBackProductComboGroupSelect}
        handleSelectProductInComboList={props.handleSelectProductInComboList}
        handleBackProductComboChild={props.handleBackProductComboChild}
        handleAddToProductCombo={props.handleAddToProductCombo}
        addToProductComboError={props.addToProductComboError}
        // Modifier Combo
        modifierComboDetails={props.modifierComboDetails}
        handleModifierComboSelect={props.handleModifierComboSelect}
        handleBackModifierComboSelect={props.handleBackModifierComboSelect}
        handleModifierComboGroupSelect={props.handleModifierComboGroupSelect}
        handleBackModifierComboGroupSelect={props.handleBackModifierComboGroupSelect}
        handleModifierChangeModComboGroup={props.handleModifierChangeModComboGroup}
        handleModifierComboAddSelectionToGroup={props.handleModifierComboAddSelectionToGroup}
        handleModComboAddComboToProduct={props.handleModComboAddComboToProduct}
        handleRemoveModifierComboFromProduct={props.handleRemoveModifierComboFromProduct}
        errorModifierCombo={props.errorModifierCombo}
      />
      <WardModal
        wardDetails={props.wardDetails}
        handleChangeWardModalFields={props.handleChangeWardModalFields}
        handleSubmitWardFields={props.handleSubmitWardFields}
      />
      {/* cartChange carries what will change if the customer switches store —
          the modal reports it instead of warning that the basket is cleared.
          See S8MC Specs\Cart Location Change. */}
      <LocationAlertModal
        alertModal={props.alertModal}
        cartChange={props.cartChange}
        handleConfirmChangeLocation={props.handleConfirmChangeLocation}
        handleCancelChangeLocation={props.handleCancelChangeLocation}
      />
      <ErrorModal
        errorModal={props.errorModal}
        handleCloseErrorModal={props.handleCloseErrorModal}
      />
      <OfflineModal
        offlineModal={props.offlineModal}
        handleCloseOfflineModal={props.handleCloseOfflineModal}
        urlConfig={props.urlConfig}
      />
    </>    
  );
}